import React, { ComponentProps, ReactNode } from 'react'
import { Link } from 'react-router-dom'

import { Box, Button, type Icon, Tooltip, useToggle } from '@factorialco/deprecated-design-system'

import Popover from 'modules/core/components/PopoverMenu'

import * as styles from '../index.vanilla.css'

export interface Action {
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  label: string
  onClick?: () => void
  to?: ComponentProps<typeof Link>['to']
  disabled?: boolean
}

export interface Option {
  title: string
  description?: string
  icon?: React.ComponentProps<typeof Icon.Medium>['icon']
  onClick?: () => void
  to?: string
  href?: React.ComponentProps<typeof Popover.HrefOption>['href']
  download?: React.ComponentProps<typeof Popover.HrefOption>['download']
  target?: React.ComponentProps<typeof Popover.HrefOption>['target']
  danger?: boolean
  asChild?: boolean
}

interface Props {
  options?: Option[]
  optionsButton?: ReactNode
  action?: Action
}

interface ButtonTypeProps {
  action: Action
}

const ButtonType = ({ action, children }: React.PropsWithChildren<ButtonTypeProps>) => {
  if (action.to) {
    return (
      <Button.Router
        to={action.to}
        disabled={action.disabled}
        onClick={action.onClick}
        iconName={action.icon}
      >
        {children as string}
      </Button.Router>
    )
  }

  return (
    <Button onClick={action.onClick} disabled={action.disabled} iconName={action.icon}>
      {children as string}
    </Button>
  )
}

const OptionType = ({ item }: { item: Option }) => {
  if (item.to) {
    return (
      <Popover.RouterOption
        title={item.title}
        description={item.description}
        to={{ pathname: item.to }}
        onClick={item.onClick}
        asChild={item.asChild}
      />
    )
  }

  if (item.href) {
    return (
      <Popover.HrefOption
        title={item.title}
        description={item.description}
        href={item.href}
        target={item.target || '_blank'}
        danger={item.danger}
        download={item.download}
      />
    )
  }

  return (
    <Popover.TouchableOption
      title={item.title}
      description={item.description}
      onClick={item.onClick}
      danger={item.danger}
    />
  )
}

const SingleOptionButton = ({ option }: { option: Option }) => {
  let button: React.ReactElement

  if (option.to) {
    button = (
      <Button.Router
        type={'secondary'}
        to={option.to}
        onClick={option.onClick}
        iconName={option.icon}
      />
    )
  } else if (option.href) {
    button = (
      <Button.Router
        type="secondary"
        to={option.href}
        blankTarget={option.target === '_blank'}
        onClick={option.onClick}
        iconName={option.icon}
      />
    )
  } else {
    button = <Button type="secondary" onClick={option.onClick} iconName={option.icon} />
  }

  const [open, toggle] = useToggle()

  return (
    <Tooltip.Dialog label={option.title} attachment="top center" open={open} toggle={toggle}>
      {button}
    </Tooltip.Dialog>
  )
}

const Options = ({ options, optionsButton }: { options: Option[]; optionsButton?: ReactNode }) => {
  if (options.length === 1 && options[0]?.icon && !options[0]?.danger && !optionsButton) {
    return <SingleOptionButton option={options[0]} />
  }

  const optionsButtonContent = () => {
    if (optionsButton) {
      return optionsButton
    }

    return <Button.Decorative type="secondary" iconName="ellipsis" />
  }

  return (
    <Popover type="list">
      <Popover.Trigger>{optionsButtonContent()}</Popover.Trigger>
      <Popover.Content side="bottom" align="center" hideWhenDetached={false}>
        {options.map((item: Option, index: number) => {
          return <OptionType item={item} key={index} />
        })}
      </Popover.Content>
    </Popover>
  )
}

const Actions = ({ action, options, optionsButton }: Props) => {
  if (!action && !options) return null

  return (
    <Box gap="s8" flexDirection="row" justifyContent="flexEnd" className={styles.right}>
      {action && <ButtonType action={action}>{action.label}</ButtonType>}
      {options && <Options options={options} optionsButton={optionsButton} />}
    </Box>
  )
}

export default Actions
